import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SKILL_CATEGORIES, type SkillCategory } from "./";

type PureCallback = (_: SkillCategory) => void;
type NullableCallback = (_: SkillCategory | null) => void;

// Generic interface that conditionally includes null based on includeNoneOption
interface SkillCategorySelectProps<T extends boolean> {
  value: T extends true ? SkillCategory | null : SkillCategory;
  onValueChange: T extends true ? NullableCallback : PureCallback;
  placeholder?: string;
  includeLabel?: boolean;
  includeNoneOption?: T;
  noneOptionLabel?: string;
  className?: string;
  disabled?: boolean;
}

export function SkillCategorySelect<T extends boolean = false>({
  value,
  onValueChange,
  placeholder = "Select category (optional)",
  includeLabel = true,
  includeNoneOption,
  noneOptionLabel = "No category",
  className,
  disabled,
}: SkillCategorySelectProps<T>) {
  const handleValueChange = (selectedValue: string) => {
    if (includeNoneOption && selectedValue === "none") {
      // When includeNoneOption is true and "none" is selected, pass null
      (onValueChange as NullableCallback)(null);
    } else if (selectedValue === "") {
      // When includeNoneOption is false and "none" is given we are in a deadend.
      throw new Error(
        "empty value is not allowed without `includeNoneOption = true`",
      );
    } else {
      // Pass the actual category value
      onValueChange(selectedValue as SkillCategory);
    }
  };

  const displayValue = value === null ? "none" : (value as string);

  return (
    <div className={className}>
      {includeLabel && <Label htmlFor="skill-category">Category</Label>}
      <Select
        value={displayValue}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {includeNoneOption && (
            <SelectItem value="none">{noneOptionLabel}</SelectItem>
          )}
          {SKILL_CATEGORIES.map((category) => (
            <SelectItem key={category} value={category}>
              {category}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
